// apps/web/src/features/russia-requests/components/tabs/CargoComplexFiles.tsx
//
// #1548 read-only file list for a COMPLEX request's cargo section. Reads
// the #1546 file surface (`GET /russia-requests/:id/files`) via TanStack
// Query. READ only — no upload/delete affordance lives here.

import { useTranslation } from 'react-i18next';
import type { RussiaRequestFile } from '@magimp/shared';

import { useRussiaRequestFiles } from '../../queries.js';

const cellHead = {
  padding: '6px 8px',
  borderBottom: '1px solid var(--border-1)',
  fontWeight: 500,
  fontSize: 12,
  color: 'var(--fg-3)',
  textAlign: 'left' as const,
};

const cell = {
  padding: '6px 8px',
  borderBottom: '1px solid var(--border-2)',
  fontSize: 13,
};

function fmtBytes(value: RussiaRequestFile['contentLength']): string {
  if (value < 1024) return `${value} B`;
  if (value < 1024 * 1024) return `${Math.round(value / 1024)} KB`;
  return `${(value / (1024 * 1024)).toFixed(1)} MB`;
}

export function CargoComplexFiles({ requestId }: { requestId: string }) {
  const { t } = useTranslation('russiaRequests');
  const query = useRussiaRequestFiles(requestId);

  if (query.isLoading) {
    return (
      <p
        data-testid="russia-request-drawer-cargo-files-loading"
        style={{ fontSize: 13, margin: 0 }}
      >
        {t('loading')}
      </p>
    );
  }
  if (query.isError) {
    return (
      <p
        data-testid="russia-request-drawer-cargo-files-error"
        role="alert"
        style={{ fontSize: 13, margin: 0 }}
      >
        {t('drawer.cargo.files.error')}
      </p>
    );
  }

  const files = query.data ?? [];
  if (files.length === 0) {
    return (
      <p
        data-testid="russia-request-drawer-cargo-files-empty"
        style={{ color: 'var(--fg-3)', fontSize: 13, margin: 0 }}
      >
        {t('drawer.cargo.files.empty')}
      </p>
    );
  }

  return (
    <table
      data-testid="russia-request-drawer-cargo-files-table"
      style={{ width: '100%', borderCollapse: 'collapse' }}
    >
      <thead>
        <tr>
          <th style={cellHead}>{t('drawer.cargo.files.columns.name')}</th>
          <th style={cellHead}>{t('drawer.cargo.files.columns.size')}</th>
        </tr>
      </thead>
      <tbody>
        {files.map((f) => (
          <tr key={f.id} data-testid={`russia-request-drawer-cargo-files-row-${f.id}`}>
            <td style={{ ...cell, wordBreak: 'break-word' }}>{f.fileName}</td>
            <td style={cell}>{fmtBytes(f.contentLength)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
